// Wellcam — Pose estimation overlay (R2 W1; AI postura v2 → MediaPipe Tasks Vision).
//
// Architettura privacy-first:
//   - L'inferenza MediaPipe Pose Landmarker gira nel BROWSER del CLIENTE
//     sul suo stream locale. Nessun video viene mandato al server per AI.
//   - I 33 landmark (joints body pose) vengono serializzati come float16
//     compatti e mandati via LiveKit data channel (kind=lossy, ~30 Hz)
//   - Il TRAINER riceve i landmark e disegna lo scheletro come canvas overlay
//     sopra la tile del cliente (no flash visivo lato cliente).
//
// API esposta come window.WellcamPose:
//   WellcamPose.startLocalDetection(videoEl, onLandmarks)
//     Carica MediaPipe lazy, avvia detection sul videoEl locale,
//     chiama onLandmarks(landmarks) ad ogni frame (~30/s)
//
//   WellcamPose.stopLocalDetection()
//     Ferma il loop e libera MediaPipe
//
//   WellcamPose.renderSkeleton(canvas, landmarks, opts)
//     Disegna scheletro sul canvas con coordinate normalizzate [0..1]
//     opts: { color, lineWidth, jointRadius, connections }
//
//   WellcamPose.encodeLandmarks(landmarks) → Uint8Array
//   WellcamPose.decodeLandmarks(Uint8Array) → landmarks
//     Encoding compatto per data channel (33 punti × 3 coord × float16 = ~200 bytes)
//
// Dipendenze runtime (caricate lazy da CDN, ESM dynamic import):
//   - @mediapipe/tasks-vision (vision_bundle.mjs + wasm) da jsDelivr
//   - modello pose_landmarker_full.task da storage.googleapis.com
//
// Performance target:
//   - ≥ 25 FPS su MacBook M1 / smartphone moderno (GPU delegate, fallback CPU)
//   - < 5% CPU su trainer (solo rendering canvas, no inferenza)

(function () {
  if (window.WellcamPose) return;

  // MediaPipe Pose POSE_CONNECTIONS (joint pairs for skeleton lines).
  // Indici corrispondono ai 33 landmark MediaPipe Pose.
  const POSE_CONNECTIONS = [
    // Faccia
    [0, 1], [1, 2], [2, 3], [3, 7],
    [0, 4], [4, 5], [5, 6], [6, 8],
    [9, 10],
    // Torso
    [11, 12], [11, 23], [12, 24], [23, 24],
    // Braccio sinistro
    [11, 13], [13, 15], [15, 17], [15, 19], [15, 21], [17, 19],
    // Braccio destro
    [12, 14], [14, 16], [16, 18], [16, 20], [16, 22], [18, 20],
    // Gamba sinistra
    [23, 25], [25, 27], [27, 29], [27, 31], [29, 31],
    // Gamba destra
    [24, 26], [26, 28], [28, 30], [28, 32], [30, 32],
  ];

  let poseLandmarker = null;
  let detectionLoop = null;
  let onLandmarksCb = null;
  let visionLoading = null;

  // Versione pinnata di @mediapipe/tasks-vision (CDN). Pin esplicito = build
  // riproducibile, niente breaking change automatici al prossimo rilascio.
  const TV_VERSION = '0.10.14';
  const TV_BASE = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${TV_VERSION}`;
  // Modello "full" (qualità ≈ modelComplexity 1 del vecchio MediaPipe Pose).
  const POSE_MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/1/pose_landmarker_full.task';

  // ===== Load MediaPipe Tasks Vision from CDN (lazy, ESM dynamic import) =====
  // Ritorna { PoseLandmarker, fileset } pronti per createFromOptions.
  function loadVision() {
    if (visionLoading) return visionLoading;
    visionLoading = (async () => {
      let mod;
      try {
        mod = await import(`${TV_BASE}/vision_bundle.mjs`);
      } catch (e) {
        visionLoading = null; // consenti un retry alla prossima chiamata
        throw new Error('Impossibile caricare MediaPipe Tasks Vision da CDN');
      }
      const { FilesetResolver, PoseLandmarker } = mod;
      const fileset = await FilesetResolver.forVisionTasks(`${TV_BASE}/wasm`);
      return { PoseLandmarker, fileset };
    })();
    return visionLoading;
  }

  // ===== Local detection (sul video del cliente) =====
  async function startLocalDetection(videoEl, onLandmarks) {
    const { PoseLandmarker, fileset } = await loadVision();
    if (poseLandmarker) stopLocalDetection();

    const baseConfig = {
      runningMode: 'VIDEO',
      numPoses: 1,
      minPoseDetectionConfidence: 0.5,
      minPosePresenceConfidence: 0.5,
      minTrackingConfidence: 0.5,
    };
    // Prova GPU delegate (più veloce); fallback a CPU se il device/browser non
    // espone un contesto WebGL utilizzabile (es. alcuni Android datati).
    try {
      poseLandmarker = await PoseLandmarker.createFromOptions(fileset, {
        ...baseConfig,
        baseOptions: { modelAssetPath: POSE_MODEL_URL, delegate: 'GPU' },
      });
    } catch (e) {
      console.warn('[wc-pose] GPU delegate non disponibile, fallback CPU', e);
      poseLandmarker = await PoseLandmarker.createFromOptions(fileset, {
        ...baseConfig,
        baseOptions: { modelAssetPath: POSE_MODEL_URL, delegate: 'CPU' },
      });
    }
    onLandmarksCb = onLandmarks;

    let running = true;
    let lastInfer = 0;
    let lastTs = -1;
    const MIN_INTERVAL_MS = 1000 / 30; // cap inferenza ~30 FPS (il sender ri-throttla a 20 Hz)
    detectionLoop = { stop: () => { running = false; } };

    function loop() {
      if (!running || !poseLandmarker) return;
      const now = performance.now();
      if (videoEl.readyState >= 2 && videoEl.videoWidth > 0 && (now - lastInfer) >= MIN_INTERVAL_MS) {
        lastInfer = now;
        // detectForVideo richiede timestamp monotòni, strettamente crescenti
        let ts = now;
        if (ts <= lastTs) ts = lastTs + 1;
        lastTs = ts;
        try {
          const result = poseLandmarker.detectForVideo(videoEl, ts);
          // result.landmarks = array di persone; [0] = prima persona,
          // array di {x,y,z,visibility} normalizzati [0..1] — stesso shape
          // che il resto del modulo (encode/render) si aspetta.
          if (onLandmarksCb && result && result.landmarks && result.landmarks.length > 0) {
            onLandmarksCb(result.landmarks[0]);
          }
        } catch (e) {
          console.warn('[wc-pose] inference error', e);
        }
      }
      requestAnimationFrame(loop);
    }
    loop();
  }

  function stopLocalDetection() {
    if (detectionLoop) detectionLoop.stop();
    detectionLoop = null;
    if (poseLandmarker) {
      try { poseLandmarker.close(); } catch {}
      poseLandmarker = null;
    }
    onLandmarksCb = null;
  }

  // ===== Render skeleton on canvas =====
  function renderSkeleton(canvas, landmarks, opts) {
    opts = opts || {};
    const color = opts.color || '#FF7F00';     // arancione dinamico Wellcam
    const lineWidth = opts.lineWidth || 3;
    const jointRadius = opts.jointRadius || 4;
    const connections = opts.connections || POSE_CONNECTIONS;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    if (!landmarks || landmarks.length === 0) return;

    // Linee scheletro
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    connections.forEach(([a, b]) => {
      const pa = landmarks[a], pb = landmarks[b];
      if (!pa || !pb) return;
      // Filtra punti a bassa visibilità
      if ((pa.visibility != null && pa.visibility < 0.3) ||
          (pb.visibility != null && pb.visibility < 0.3)) return;
      ctx.beginPath();
      ctx.moveTo(pa.x * W, pa.y * H);
      ctx.lineTo(pb.x * W, pb.y * H);
      ctx.stroke();
    });

    // Punti joint
    ctx.fillStyle = '#fff';
    landmarks.forEach((p) => {
      if (!p) return;
      if (p.visibility != null && p.visibility < 0.3) return;
      ctx.beginPath();
      ctx.arc(p.x * W, p.y * H, jointRadius, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  // ===== Encoding compatto landmarks per data channel =====
  // Format binario:
  //   [0..1]   magic byte 0x57 + 0x50 ("WP" = Wellcam Pose)
  //   [2]      count (uint8, max 33)
  //   [3..]    per landmark: 2× float16 (x, y) + 1× uint8 visibility (0..255) = 5 bytes
  // Totale: 2 + 1 + 33*5 = 168 bytes per frame (vs ~500 bytes JSON)
  function encodeLandmarks(landmarks) {
    const n = Math.min(landmarks.length, 33);
    const buf = new Uint8Array(3 + n * 5);
    buf[0] = 0x57; buf[1] = 0x50;
    buf[2] = n;
    for (let i = 0; i < n; i++) {
      const p = landmarks[i];
      const x16 = float16(p.x);
      const y16 = float16(p.y);
      const off = 3 + i * 5;
      buf[off] = x16 & 0xff;
      buf[off + 1] = (x16 >> 8) & 0xff;
      buf[off + 2] = y16 & 0xff;
      buf[off + 3] = (y16 >> 8) & 0xff;
      const vis = Math.max(0, Math.min(1, p.visibility != null ? p.visibility : 1));
      buf[off + 4] = Math.round(vis * 255);
    }
    return buf;
  }

  function decodeLandmarks(buf) {
    if (!buf || buf.length < 3) return null;
    if (buf[0] !== 0x57 || buf[1] !== 0x50) return null;
    const n = buf[2];
    if (buf.length < 3 + n * 5) return null;
    const out = new Array(n);
    for (let i = 0; i < n; i++) {
      const off = 3 + i * 5;
      const xi = buf[off] | (buf[off + 1] << 8);
      const yi = buf[off + 2] | (buf[off + 3] << 8);
      out[i] = {
        x: defloat16(xi),
        y: defloat16(yi),
        visibility: buf[off + 4] / 255,
      };
    }
    return out;
  }

  // Half-precision float (IEEE 754 binary16). Lossy ma precisione ~0.001 sufficiente per pose.
  function float16(val) {
    const f = new Float32Array(1);
    const i = new Int32Array(f.buffer);
    f[0] = val;
    const x = i[0];
    const sign = (x >>> 16) & 0x8000;
    let exp = ((x >>> 23) & 0xff) - 127 + 15;
    let mant = x & 0x7fffff;
    if (exp >= 31) { return sign | 0x7c00; }
    if (exp <= 0) {
      if (exp < -10) return sign;
      mant = (mant | 0x800000) >> (1 - exp);
      return sign | (mant >> 13);
    }
    return sign | (exp << 10) | (mant >> 13);
  }
  function defloat16(h) {
    const sign = (h & 0x8000) << 16;
    const exp = (h & 0x7c00) >>> 10;
    const mant = (h & 0x03ff);
    let f32;
    if (exp === 0) {
      if (mant === 0) f32 = sign;
      else {
        // Subnormal: convert
        let e = -1;
        let m = mant;
        do { e++; m <<= 1; } while ((m & 0x400) === 0);
        f32 = sign | ((127 - 15 - e) << 23) | ((m & 0x3ff) << 13);
      }
    } else if (exp === 31) {
      f32 = sign | 0x7f800000 | (mant << 13);
    } else {
      f32 = sign | ((exp + 127 - 15) << 23) | (mant << 13);
    }
    const i = new Int32Array(1);
    const f = new Float32Array(i.buffer);
    i[0] = f32;
    return f[0];
  }

  window.WellcamPose = {
    startLocalDetection,
    stopLocalDetection,
    renderSkeleton,
    encodeLandmarks,
    decodeLandmarks,
    POSE_CONNECTIONS,
  };
})();
